/**
 * VoiceLevelIndicator - Visual microphone level meter
 *
 * Renders a set of vertical bars whose height responds to the
 * normalised audio level (0-1) from useAudioLevel.  Gives the
 * patient immediate feedback that their voice is being picked up
 * and a rough sense of whether they're speaking loudly enough.
 *
 * Props:
 *   level          - number 0-1, current audio RMS level
 *   barCount       - number of bars (default 5)
 *   height         - overall height in px (default 28)
 *   color          - bar colour when active (default theme success)
 *   muted          - if true, show muted state
 *   userHasSpoken  - if true, user has already said something; when level is low show "Listening..." instead of "Speak now" / "Too quiet"
 */

import { tokens } from 'utils/tokens';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

// The raw audio level jitters several times a second; sampling the text label
// on this cadence keeps it readable while the bars still react in real time.
const LABEL_SAMPLE_MS = 700;

const BAR_COUNT_DEFAULT = 5;
const HEIGHT_DEFAULT = 28;
const BRAND_MAROON = tokens.colors.msiRed.light;
// The brand maroon is unreadable on a dark background; dark mode falls back to the soft error red.
const mutedColor = (theme) =>
  theme.palette.mode === 'dark' ? theme.palette.error.light : BRAND_MAROON;

function VoiceLevelIndicator({
  level = 0,
  barCount = BAR_COUNT_DEFAULT,
  height = HEIGHT_DEFAULT,
  // Defaulted explicitly so TypeScript callers see it as optional. The real fallback is the
  // level-dependent colour computed below; this only stops the inferred prop type from
  // demanding a colour the component does not need.
  color = undefined,
  muted = false,
  userHasSpoken = false,
}) {
  // Map the 0-1 level to bar height; no extra boost so near-silence stays low (useAudioLevel already gates noise floor)
  const boosted = useMemo(() => Math.min(1, level * 1.2), [level]);

  const bars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < barCount; i++) {
      // Each bar has a threshold; bars fill left-to-right as level rises
      const threshold = (i + 1) / barCount;
      const active = boosted >= threshold;
      // Partial fill for the "current" bar
      const fraction = active
        ? 1
        : Math.max(0, (boosted - (i / barCount)) * barCount);
      arr.push({ active, fraction });
    }
    return arr;
  }, [boosted, barCount]);

  // Keep the latest inputs in a ref so the slow sampler below reads current
  // values without restarting its interval on every level change.
  const labelInputsRef = useRef({ level, muted, userHasSpoken });
  labelInputsRef.current = { level, muted, userHasSpoken };

  const computeLabel = ({ level: lvl, muted: m, userHasSpoken: spoke }) => {
    if (m) return 'Please wait';
    // After user has spoken, low level = we heard them / processing, not "speak up"
    if (spoke && lvl < 0.12) return 'Listening...';
    if (lvl < 0.04) return 'Speak now...';
    // Two words, like the other rungs. The long form ('Quiet — try speaking louder') was the
    // only label that ran to a sentence, so it resized the label column every time the voice
    // dipped, and it was the only user-facing string here carrying an em dash.
    if (lvl < 0.12) return 'Too quiet';
    if (lvl < 0.35) return 'Good volume';
    return 'Great!';
  };

  const [label, setLabel] = useState(() => computeLabel(labelInputsRef.current));

  // Muted is a discrete state (assistant speaking / input paused), not noisy —
  // reflect it immediately so "Please wait" appears the moment it applies.
  useEffect(() => {
    if (muted) setLabel('Please wait');
  }, [muted]);

  // Sample the noisy level-driven label on a slow cadence so it stops flickering.
  useEffect(() => {
    const id = setInterval(() => setLabel(computeLabel(labelInputsRef.current)), LABEL_SAMPLE_MS);
    return () => clearInterval(id);
  }, []);

  // Orange/green status colours read on both light and dark backgrounds.
  const barColor = color || (level < 0.12 ? '#fb8c00' : '#43a047');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: 'action.hover',
        width: 'fit-content',
      }}
    >
      {muted ? (
        <MicOffIcon
          fontSize="small"
          sx={{ color: mutedColor, transition: 'color 0.2s' }}
        />
      ) : (
        <MicIcon
          fontSize="small"
          sx={{ color: barColor, transition: 'color 0.2s' }}
        />
      )}

      {/* Live level bars — only while it's actually the user's turn. The icon and
          text stay put so they always know the mic's state (e.g. "Please wait"). */}
      {!muted && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height }}>
          {bars.map((bar, i) => (
            <Box
              key={i}
              sx={{
                width: 4,
                borderRadius: 1,
                bgcolor: bar.fraction > 0 ? barColor : 'action.disabledBackground',
                height: Math.max(height * 0.15, bar.fraction * height),
                transition: 'height 0.08s ease-out, background-color 0.15s',
              }}
            />
          ))}
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{
          color: muted ? mutedColor : 'text.secondary',
          ml: 0.5,
          minWidth: 80,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default React.memo(VoiceLevelIndicator);
